import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, X, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../components/CustomToast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManagePasswordModal({ isOpen, onClose }) {
  const { token, logout } = useAuth();

  // Form fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  // Visibility toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);

  // Status
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  const currentRef = useRef(null);

  // Validation rules
  const hasLowerUpper = /(?=.*[a-z])(?=.*[A-Z])/.test(newPassword);
  const hasNumberOrSymbol = /(?=.*[0-9!@#$%^&*()_+{}:"<>?|~`\-\=[\]\\;',./])/.test(newPassword);
  const isMinLength = newPassword.length >= 6;
  const isMatch = newPassword.length > 0 && newPassword === repeatPassword;
  const noSpacesOrPipe = newPassword.length > 0 && !/[\s|]/.test(newPassword);

  const canSubmit = isVerified && hasLowerUpper && hasNumberOrSymbol && isMinLength && isMatch && noSpacesOrPipe && !loading;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowRepeat(false);
      setIsVerified(false);
      setIsVerifying(false);
      setLoading(false);
      setTimeout(() => currentRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Auto-verify current password with debounce
  useEffect(() => {
    if (!currentPassword) {
      setIsVerified(false);
      return;
    }
    
    // Once verified, if they change it, reset verified state
    setIsVerified(false);
    
    if (currentPassword.length < 1) return; // Verify as soon as any character is typed

    const timer = setTimeout(async () => {
      setIsVerifying(true);
      try {
        await axios.post(
          `${API_URL}/api/v1/auth/verify-password`,
          { current_password: currentPassword },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsVerified(true);
      } catch (err) {
        setIsVerified(false);
      } finally {
        setIsVerifying(false);
      }
    }, 600);
    
    return () => clearTimeout(timer);
  }, [currentPassword, token]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/v1/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password updated successfully. Please log in again.');
      logout();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update password');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // ─── Shared Styles ────────────────────────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    animation: 'fadeIn 0.2s ease',
  };

  const modalStyle = {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '440px',
    margin: '0 16px',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
    animation: 'slideUp 0.25s ease',
    overflow: 'hidden',
    position: 'relative',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px 24px 16px',
  };

  const bodyStyle = {
    padding: '0 24px 24px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#475569',
    marginBottom: '6px',
  };

  const inputWrapperStyle = {
    position: 'relative',
    marginBottom: '20px',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 42px 12px 16px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#0f172a',
    background: '#fff',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  };

  const inputFocusStyle = {
    borderColor: '#7c3aed',
    boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
  };

  const inputDisabledStyle = {
    background: '#f8fafc',
    color: '#94a3b8',
    cursor: 'not-allowed',
    borderColor: '#e2e8f0',
  };

  const eyeButtonStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px',
    transition: 'color 0.15s',
  };

  const ruleTextStyle = (isValid) => ({
    color: isValid ? '#16a34a' : '#64748b',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
    transition: 'color 0.2s ease',
  });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ fontWeight: 600, fontSize: '20px', color: '#0f172a' }}>
            Change Password
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              padding: '4px',
              borderRadius: '8px',
              display: 'flex',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#0f172a')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          <form onSubmit={handleSave}>
            
            {/* Current Password */}
            <div style={inputWrapperStyle}>
              <label style={labelStyle}>Current Password:</label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={currentRef}
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: isVerified ? '#22c55e' : (currentPassword && !isVerifying && !isVerified && currentPassword.length >= 1 ? '#ef4444' : '#cbd5e1'),
                    boxShadow: isVerified ? '0 0 0 1px #22c55e' : (currentPassword && !isVerifying && !isVerified && currentPassword.length >= 1 ? '0 0 0 1px #ef4444' : 'none'),
                    paddingRight: '60px' // Make room for loader/check + eye icon
                  }}
                  onFocus={(e) => {
                    Object.assign(e.target.style, inputFocusStyle);
                    if (isVerified) {
                      e.target.style.borderColor = '#22c55e';
                      e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.15)';
                    } else if (currentPassword && !isVerifying && !isVerified && currentPassword.length >= 1) {
                      e.target.style.borderColor = '#ef4444';
                      e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.15)';
                    }
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isVerified ? '#22c55e' : (currentPassword && !isVerifying && !isVerified && currentPassword.length >= 1 ? '#ef4444' : '#cbd5e1');
                    e.target.style.boxShadow = isVerified ? '0 0 0 1px #22c55e' : (currentPassword && !isVerifying && !isVerified && currentPassword.length >= 1 ? '0 0 0 1px #ef4444' : 'none');
                  }}
                />
                
                {/* Status Indicator for Verification */}
                <div style={{
                  position: 'absolute',
                  right: '40px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {isVerifying && <Loader2 size={16} color="#94a3b8" style={{ animation: 'spin 1s linear infinite' }} />}
                  {isVerified && <Check size={16} color="#22c55e" />}
                  {currentPassword && !isVerifying && !isVerified && currentPassword.length >= 1 && <X size={16} color="#ef4444" />}
                </div>

                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  style={eyeButtonStyle}
                  tabIndex={-1}
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div style={inputWrapperStyle}>
              <label style={labelStyle}>New Password:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={!isVerified}
                  style={{
                    ...inputStyle,
                    ...(!isVerified ? inputDisabledStyle : {})
                  }}
                  onFocus={(e) => {
                    if (isVerified) Object.assign(e.target.style, inputFocusStyle);
                  }}
                  onBlur={(e) => {
                    if (isVerified) {
                      e.target.style.borderColor = '#cbd5e1';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  style={eyeButtonStyle}
                  tabIndex={-1}
                  disabled={!isVerified}
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Repeat Password */}
            <div style={inputWrapperStyle}>
              <label style={labelStyle}>Repeat Password:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showRepeat ? 'text' : 'password'}
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  disabled={!isVerified}
                  style={{
                    ...inputStyle,
                    ...(!isVerified ? inputDisabledStyle : {})
                  }}
                  onFocus={(e) => {
                    if (isVerified) Object.assign(e.target.style, inputFocusStyle);
                  }}
                  onBlur={(e) => {
                    if (isVerified) {
                      e.target.style.borderColor = '#cbd5e1';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowRepeat((v) => !v)}
                  style={eyeButtonStyle}
                  tabIndex={-1}
                  disabled={!isVerified}
                >
                  {showRepeat ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '24px',
                  background: canSubmit ? 'linear-gradient(to right, #7c3aed, #7e22ce)' : '#e2e8f0', // using gradient from schedule call button
                  color: canSubmit ? '#fff' : '#94a3b8',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { if (canSubmit) e.target.style.background = 'linear-gradient(to right, #6d28d9, #6b21a8)'; }}
                onMouseLeave={(e) => { if (canSubmit) e.target.style.background = 'linear-gradient(to right, #7c3aed, #7e22ce)'; }}
              >
                {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                Save
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '24px',
                  background: '#fff',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.target.style.background = '#fff'}
              >
                Cancel
              </button>
            </div>

            {/* Password Rules */}
            <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '8px', fontWeight: 'bold' }}>
              Password Must:
            </div>
            <div style={{ paddingLeft: '4px' }}>
              <div style={ruleTextStyle(newPassword.length > 0 && hasLowerUpper)}>
                - Include lower and upper characters
              </div>
              <div style={ruleTextStyle(newPassword.length > 0 && hasNumberOrSymbol)}>
                - Include at least 1 number or symbol
              </div>
              <div style={ruleTextStyle(newPassword.length > 0 && isMinLength)}>
                - Be at least 6 characters long
              </div>
              <div style={ruleTextStyle(repeatPassword.length > 0 && isMatch)}>
                - Match in both fields
              </div>
              <div style={ruleTextStyle(newPassword.length > 0 && noSpacesOrPipe)}>
                - Cannot contain spaces and "|" symbol
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Keyframe animations injected inline */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}